'use client';

import React, { useState, useEffect } from 'react';
import api, { Settings as SettingsType, ParameterSettings, WeclappSettings, Warehouse } from '@/lib/api';
import {
  Loader2,
  Save,
  Bell,
  Sliders,
  Link2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Wifi,
  Settings as SettingsIcon,
  Warehouse as WarehouseIcon, Package, Ban,
} from 'lucide-react';

// Parse a numeric input while keeping an explicit 0. Plain `parseX(v) || default`
// discards a typed 0 (0 is falsy), so a user can never store 0 for a weight or
// safety-days field. Fall back to the default only for empty/invalid input.
// Pass `min` for parameters where 0 is not a valid value (e.g. lookbackDays=0
// would collapse the consumption buckets and corrupt every metric).
const numOr = (raw: string, fallback: number, min?: number): number => {
  const value = Number(raw);
  if (raw.trim() === '' || Number.isNaN(value)) {
    return fallback;
  }
  return min !== undefined && value < min ? min : value;
};

function formatIntervalLabel(hours: number): string {
  if (hours === 0) return 'Deaktiviert';
  if (hours === 24) return 'Täglich (24 Stunden)';
  if (hours === 168) return 'Wöchentlich (7 Tage)';
  const days = hours / 24;
  return `Alle ${days} Tage`;
}

export default function Settings() {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [computing, setComputing] = useState(false);

  // Warehouse state
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [shippingWarehouses, setShippingWarehouses] = useState<Record<string, boolean>>({});
  const [warehouseResult, setWarehouseResult] = useState<{ success: boolean; message: string } | null>(null);

  // Form states
  const [notifEmail, setNotifEmail] = useState('');
  const [generalSettingsError, setGeneralSettingsError] = useState<string | null>(null);
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [notifIntervalHours, setNotifIntervalHours] = useState<number>(24);
  const [notifIntervalOptions, setNotifIntervalOptions] = useState<number[]>([0, 24, 48, 72, 168]);
  const [adhocEnabled, setAdhocEnabled] = useState<boolean>(true);
  const [params, setParams] = useState<ParameterSettings>({
    lookbackDays: 90,
    weight0_30: 0.5,
    weight30_60: 0.3,
    weight60_90: 0.2,
    safetyDays: 7,
    targetCoverDays: 30,
    defaultLeadTimeDays: 30,
    minDailyUsageFloor: 0.1,
    overstockFactor: 2.0,
    noSalesDays: 60,
    includeOpenPosInRecommendation: true,
    safetyStockServiceLevel: 'off',
    overdueIncomingGraceDays: 90,
  });
  const [weclappUrl, setWeclappUrl] = useState('');
  const [weclappToken, setWeclappToken] = useState('');
  const [monitoredProductCategoryIds, setMonitoredProductCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const [s, whs] = await Promise.all([api.getSettings(), api.getWarehouses()]);
        if (!mounted) return;

        setSettings(s);
        setNotifEmail(s.notificationEmail || '');
        // setTimezone(s.timezone || 'Europe/Berlin');
        if (typeof s.notificationIntervalHours === 'number') setNotifIntervalHours(s.notificationIntervalHours);
        if (Array.isArray(s.notificationIntervalOptions) && s.notificationIntervalOptions.length) {
          setNotifIntervalOptions(s.notificationIntervalOptions);
        }
        if (typeof s.adhocNotificationsEnabled === 'boolean') setAdhocEnabled(s.adhocNotificationsEnabled);
        if (s.parameters) setParams(s.parameters);
        setMonitoredProductCategoryIds(s.monitoredProductCategoryIds || []);
        if (s.weclapp) {
          setWeclappUrl(s.weclapp.baseUrl || '');
        }

        setWarehouses(whs);
        const shippingMap: Record<string, boolean> = {};
        for (const wh of whs) {
          shippingMap[wh.id] = wh.shippingWarehouse;
        }
        setShippingWarehouses(shippingMap);
      } catch (e) {
        console.error('getSettings failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const saveGeneral = async () => {
    setGeneralSettingsError(null);
    setSaving('general');
    try {
      await api.updateGeneralSettings({
        notificationEmail: notifEmail,
        timezone: timezone,
        notificationIntervalHours: notifIntervalHours,
        adhocNotificationsEnabled: adhocEnabled,
        monitoredProductCategoryIds,
      });
    } catch (err) {
      setGeneralSettingsError(err instanceof Error ? err.message : 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(null);
    }
  };

  const saveParams = async () => {
    setSaving('params');
    try {
      await api.updateParameters(params);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const saveWeclapp = async () => {
    setSaving('weclapp');
    try {
      await api.updateWeclapp({ baseUrl: weclappUrl, apiToken: weclappToken });
      setWeclappToken('');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const testConnection = async () => {
    setTestResult(null);
    setSaving('test');
    try {
      const result = await api.testWeclapp();
      setTestResult({ success: result.success, message: result.message });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setTestResult({ success: false, message: msg });
    } finally {
      setSaving(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.triggerSync();
    } catch (err) {
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleCompute = async () => {
    setComputing(true);
    try {
      await api.triggerCompution();
    } catch (err) {
      console.error(err);
    } finally {
      setComputing(false);
    }
  };

  const saveWarehouses = async () => {
    setSaving('warehouses');
    setWarehouseResult(null);
    let selectionSaved = false;
    try {
      const updates = warehouses.map((wh) => ({ id: wh.id, shippingWarehouse: shippingWarehouses[wh.id] ?? wh.shippingWarehouse }));
      await api.updateShippingWarehouses(updates);
      selectionSaved = true;
      // Reservations are scoped during ERP sync. Refresh them before calculating coverage.
      await api.triggerSync();
      setWarehouseResult({ success: true, message: 'Versandlager gespeichert. Bestände, Reservierungen und Reichweiten wurden aktualisiert.' });
    } catch (err) {
      setWarehouseResult({ success: false, message: selectionSaved
        ? 'Versandlager gespeichert, Aktualisierung fehlgeschlagen. Bitte Synchronisierung erneut starten; angezeigte Reichweiten sind noch nicht aktualisiert.'
        : err instanceof Error ? err.message : 'Versandlager konnten nicht gespeichert werden.' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Einstellungen</h1>
        </div>
      </div>

      {/* weclapp Connection */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Link2 className="h-4 w-4 text-brand" />
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">weclapp Verbindung</h2>
          {settings?.weclapp?.isConnected && (
            <span className="inline-flex items-center gap-1 text-xs font-bold bg-brand-soft text-brand-deep px-2.5 py-0.5 rounded-full ml-1">
              <Wifi className="h-3 w-3" /> Verbunden
            </span>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          {settings?.weclapp?.lastSyncAt && (
            <div className="bg-surface-2 rounded-panel px-4 py-3 text-[12.5px] text-ink-muted">
              <span>Letzter Sync:</span>{' '}
              <span className="font-medium text-ink">{new Date(settings.weclapp.lastSyncAt).toLocaleString('de-DE')}</span>
              {' · '}
              <span className={settings.weclapp.lastSyncStatus === 'success' ? 'text-brand-deep font-semibold' : 'text-risk-red font-semibold'}>
                {settings.weclapp.lastSyncStatus}
              </span>
              {settings.weclapp.lastSyncError && (
                <p className="text-risk-red text-[11px] mt-1">{settings.weclapp.lastSyncError}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Base URL</label>
            <input
              type="url"
              value={weclappUrl}
              onChange={(e) => setWeclappUrl(e.target.value)}
              className="input-field"
              placeholder="https://meine-firma.weclapp.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">API Token</label>
            <input
              type="password"
              value={weclappToken}
              onChange={(e) => setWeclappToken(e.target.value)}
              className="input-field"
              placeholder="Token eingeben (wird verschlüsselt gespeichert)"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={saveWeclapp} disabled={saving === 'weclapp'} className="btn-primary inline-flex items-center">
              {saving === 'weclapp' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Verbindung speichern
            </button>
            <button onClick={testConnection} disabled={saving === 'test'} className="btn-secondary inline-flex items-center">
              {saving === 'test' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wifi className="h-4 w-4 mr-2" />}
              Verbindung testen
            </button>
            <button onClick={handleSync} disabled={syncing} className="btn-secondary inline-flex items-center">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Manueller Sync
            </button>
            <button onClick={handleCompute} disabled={computing} className="btn-secondary inline-flex items-center">
              {computing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Berechnen
            </button>
          </div>

          {testResult && (
            <div className={`rounded-panel px-4 py-2.5 flex items-center gap-2 text-[12.5px] border ${testResult.success ? 'bg-brand-soft text-brand-deep border-brand-soft-border' : 'bg-risk-red-soft text-risk-red border-risk-red/20'}`}>
              {testResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {testResult.message || (testResult.success ? 'Verbindung erfolgreich!' : 'Verbindung fehlgeschlagen')}
            </div>
          )}
        </div>
      </div>

      {/* General Settings */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Bell className="h-4 w-4 text-brand" />
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Benachrichtigungen</h2>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label htmlFor="notification-emails" className="block text-sm font-semibold text-ink mb-1">Benachrichtigungs-E-Mails</label>
            <input
              id="notification-emails"
              type="text"
              inputMode="email"
              aria-describedby="notification-emails-hint"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
              className="input-field"
              placeholder="alerts@example.com; einkauf@example.com"
            />
            <p id="notification-emails-hint" className="text-[12.5px] text-ink-muted mt-1">E-Mail-Adressen für Bestandswarnungen. Mehrere Adressen mit Semikolon (;) trennen.</p>
            {generalSettingsError && (
              <p role="alert" className="text-sm text-red-600 mt-2">{generalSettingsError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Benachrichtigungs-Intervall</label>
            <select
              value={notifIntervalHours}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                setNotifIntervalHours(Number.isNaN(parsed) ? 24 : parsed);
              }}
              className="input-field"
            >
              {notifIntervalOptions.map((h) => (
                <option key={h} value={h}>{formatIntervalLabel(h)}</option>
              ))}
            </select>
            <p className="text-[12.5px] text-ink-muted mt-1">
              Wie oft eine gebündelte Übersicht mit allen aktuell kritischen Artikeln kommt.
              {notifIntervalHours === 0 && ' Deaktiviert: Es kommt keine regelmäßige Übersicht, nur Sofort-Benachrichtigungen.'}
            </p>
          </div>
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={adhocEnabled}
                onChange={(e) => setAdhocEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line"
                style={{ accentColor: '#00D68F' }}
              />
              <span>
                <span className="block text-sm font-semibold text-ink">Sofort-Benachrichtigung bei neu kritischen Artikeln</span>
                <span className="block text-[12.5px] text-ink-muted mt-0.5">
                  Sendet sofort eine E-Mail, sobald ein Artikel kritisch (Rot) wird, unabhängig vom Intervall. Pro Artikel einmal, erneut erst nachdem er wieder im grünen Bereich war.
                </span>
              </span>
            </label>
          </div>
          {/*<div>*/}
          {/*  <label className="block text-sm font-semibold text-ink mb-1">Zeitzone</label>*/}
          {/*  <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="input-field">*/}
          {/*    <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>*/}
          {/*    <option value="Europe/Zurich">Europe/Zurich</option>*/}
          {/*    <option value="Europe/Vienna">Europe/Vienna</option>*/}
          {/*    <option value="UTC">UTC</option>*/}
          {/*  </select>*/}
          {/*</div>*/}
          <div className="flex justify-end">
            <button onClick={saveGeneral} disabled={saving === 'general'} className="btn-primary inline-flex items-center">
              {saving === 'general' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Speichern
            </button>
          </div>
        </div>
      </div>

      {/* Parameters */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Sliders className="h-4 w-4 text-brand" />
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Berechnungsparameter</h2>
        </div>
        <div className="px-5 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Lookback Tage</label>
              <input type="number" value={params.lookbackDays} onChange={(e) => setParams((p) => ({ ...p, lookbackDays: numOr(e.target.value, 90, 1) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Min. Tagesverbrauch</label>
              <input type="number" step="0.01" value={params.minDailyUsageFloor} onChange={(e) => setParams((p) => ({ ...p, minDailyUsageFloor: numOr(e.target.value, 0.1) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Gewicht 0-30 Tage</label>
              <input type="number" step="0.1" value={params.weight0_30} onChange={(e) => setParams((p) => ({ ...p, weight0_30: numOr(e.target.value, 0.5) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Gewicht 30-60 Tage</label>
              <input type="number" step="0.1" value={params.weight30_60} onChange={(e) => setParams((p) => ({ ...p, weight30_60: numOr(e.target.value, 0.3) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Gewicht 60-90 Tage</label>
              <input type="number" step="0.1" value={params.weight60_90} onChange={(e) => setParams((p) => ({ ...p, weight60_90: numOr(e.target.value, 0.2) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Sicherheitstage</label>
              <input type="number" value={params.safetyDays} onChange={(e) => setParams((p) => ({ ...p, safetyDays: numOr(e.target.value, 7) }))} className="input-field" />
              <p className="text-[12.5px] text-ink-muted mt-1">
                Nur Warnschwelle der Ampel (Gelb ab Lieferzeit + Sicherheitstage) — keine Bestellmenge.
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Ziel-Reichweite (Tage)</label>
              <input type="number" value={params.targetCoverDays} onChange={(e) => setParams((p) => ({ ...p, targetCoverDays: numOr(e.target.value, 30, 1) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Standard-Lieferzeit (Tage)</label>
              <input type="number" value={params.defaultLeadTimeDays} onChange={(e) => setParams((p) => ({ ...p, defaultLeadTimeDays: numOr(e.target.value, 30, 1) }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Überbestand-Faktor</label>
              <input type="number" step="0.1" value={params.overstockFactor} onChange={(e) => setParams((p) => ({ ...p, overstockFactor: numOr(e.target.value, 2.0, 1) }))} className="input-field" />
              <p className="text-[12.5px] text-ink-muted mt-1">Reichweite &gt; Ziel-Reichweite × Faktor = Überbestand</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Keine-Verkäufe-Zeitraum (Tage)</label>
              <input type="number" value={params.noSalesDays} onChange={(e) => setParams((p) => ({ ...p, noSalesDays: numOr(e.target.value, 60, 1) }))} className="input-field" />
              <p className="text-[12.5px] text-ink-muted mt-1">Kein Verkauf innerhalb dieser Tage</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">Zulauf-Altersgrenze (Tage)</label>
              <input
                type="number"
                value={params.overdueIncomingGraceDays}
                onChange={(e) => setParams((p) => ({ ...p, overdueIncomingGraceDays: numOr(e.target.value, 90, 1) }))}
                className="input-field"
              />
              <p className="text-[12.5px] text-ink-muted mt-1">
                Bestellpositionen mit einem Liefertermin, der länger als diese Grenze zurückliegt, zählen nicht mehr in die
                Bestellempfehlung — die Ware gilt als vermutlich tot, solange niemand die Bestellung in weclapp schließt. Das
                ist das normale Instrument, um veraltete Bestellungen auszusteuern. Der Schalter „Offene Bestellungen
                verrechnen“ daneben ist der Notaus: Er blendet den gesamten Zulauf aus, nicht nur die alten Positionen.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-ink mb-1">Servicegrad Sicherheitsbestand</label>
              <select
                value={params.safetyStockServiceLevel}
                onChange={(e) => setParams((p) => ({ ...p, safetyStockServiceLevel: e.target.value }))}
                className="input-field"
              >
                <option value="off">Aus (kein Sicherheitsbestand)</option>
                <option value="90">90 %</option>
                <option value="95">95 %</option>
                <option value="97.5">97,5 %</option>
                <option value="98">98 %</option>
                <option value="99">99 %</option>
              </select>
              <p className="text-[12.5px] text-ink-muted mt-1">
                Puffer gegen Nachfrage-Schwankung: z × Standardabweichung × √Lieferzeit. „Aus" lässt die
                Bestellempfehlung unverändert. Artikel mit weniger als 10 Verbrauchstagen im Beobachtungsfenster
                erhalten keinen Puffer. Lieferzeit-Schwankung fließt nicht ein — dafür fehlen die Ist-Daten.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                <input
                  type="checkbox"
                  checked={params.includeOpenPosInRecommendation}
                  onChange={(e) => setParams((p) => ({ ...p, includeOpenPosInRecommendation: e.target.checked }))}
                />
                Offene Bestellungen in Bestellempfehlung verrechnen
              </label>
              <p className="text-[12.5px] text-ink-muted mt-1">
                Bestätigte Lieferanten-Bestellungen reduzieren die empfohlene Bestellmenge. Reichweite und Ampel bleiben auf dem physischen Bestand.
                Zum Ausblenden einzelner zu alter Bestellungen die Zulauf-Altersgrenze verwenden, nicht diesen Schalter.
              </p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={saveParams} disabled={saving === 'params'} className="btn-primary inline-flex items-center">
              {saving === 'params' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Speichern
            </button>
          </div>
        </div>
      </div>

      {/* Lager */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <WarehouseIcon className="h-4 w-4 text-brand" />
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Lagerbestände & Versandlager</h2>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="text-[12.5px] text-ink-muted mb-3">Bestände aller Lager bleiben sichtbar. Wähle zusätzlich die Versandlager aus, die für Reichweite und Warnungen überwacht werden.</p>
            <div className="max-h-48 overflow-auto bg-surface-2 rounded-panel p-3 space-y-2">
              {warehouses.length ? warehouses.map((wh) => (
                <label key={wh.id} className="flex items-center gap-2.5 text-sm text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shippingWarehouses[wh.id] ?? wh.shippingWarehouse}
                    disabled={!wh.active || saving === 'warehouses'}
                    aria-label={`${wh.name} als Versandlager überwachen`}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShippingWarehouses((current) => ({ ...current, [wh.id]: checked }));
                    }}
                    style={{ accentColor: '#00D68F' }}
                  />
                  <span className="flex-1">{wh.name}</span>
                  {!wh.active && <span className="text-[10px] text-ink-soft">Inaktiv</span>}
                  {wh.warehouseType && wh.warehouseType !== 'STANDARD' && (
                    <span className="font-mono text-[10px] text-ink-soft">{wh.warehouseType}</span>
                  )}
                </label>
              )) : <p className="text-[12.5px] text-ink-muted">Keine Lager verfügbar. Bitte zuerst einen Sync durchführen.</p>}
            </div>
            <p className="text-[12.5px] text-ink-muted mt-1">Nur ausgewählte, aktive Versandlager zählen für verfügbaren Bestand, Reichweite und Bestellempfehlungen. Bestände in anderen Lagern, z. B. FBA oder Linden Chemie, bleiben in der Bestandsübersicht sichtbar und zählen nicht zur Versandreichweite. Der Bedarf basiert weiterhin auf dem gesamten Artikelabsatz.</p>
            <p className="text-[12.5px] text-ink-muted mt-1">Beim Speichern werden Daten synchronisiert und Reichweiten neu berechnet. Neue Lager müssen als Versandlager ausgewählt werden. Ohne ausgewähltes Versandlager beträgt der berücksichtigte Bestand 0.</p>
          </div>
          {warehouseResult && <p role="status" className={`text-sm ${warehouseResult.success ? 'text-brand' : 'text-red-600'}`}>{warehouseResult.message}</p>}
          <div className="flex justify-end">
            <button onClick={saveWarehouses} disabled={saving === 'warehouses' || warehouses.length === 0} className="btn-primary inline-flex items-center">
              {saving === 'warehouses' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {saving === 'warehouses' ? 'Aktualisierung läuft…' : 'Versandlager speichern & aktualisieren'}
            </button>
          </div>
        </div>
      </div>

      {/* Warengruppen */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Package className="h-4 w-4 text-brand" />
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Warengruppen</h2>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="max-h-48 overflow-auto bg-surface-2 rounded-panel p-3 space-y-2">
              {settings?.productCategories?.length ? settings.productCategories.map((category) => (
                <label key={category.id} className="flex items-center gap-2.5 text-sm text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    checked={monitoredProductCategoryIds.includes(category.id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setMonitoredProductCategoryIds((current) =>
                        checked
                          ? [...current, category.id]
                          : current.filter((id) => id !== category.id)
                      );
                    }}
                    style={{ accentColor: '#00D68F' }}
                  />
                  {category.name}
                </label>
              )) : <p className="text-[12.5px] text-ink-muted">Keine Warengruppen verfügbar.</p>}
            </div>
            <p className="text-[12.5px] text-ink-muted mt-1">Nur ausgewählte Warengruppen erscheinen in den Listen. Leere Auswahl = alle. Nicht ausgewählte Artikel werden weiterhin berechnet und versorgen ihre Stücklisten-Komponenten — sie bekommen nur keine eigene Zeile.</p>
          </div>
          <div className="flex justify-end">
            <button onClick={saveGeneral} disabled={saving === 'general'} className="btn-primary inline-flex items-center">
              {saving === 'general' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Speichern
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
